import React, { useState } from 'react';
import { Modal, Upload, Button, message, Space, Table, Alert, Statistic, Row, Col } from 'antd';
import { UploadOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import ColaboradorService from 'services/ColaboradorService';

const PASO_CARGA = 'carga';
const PASO_RESULTADO = 'resultado';

export function ModalActualizarAprobadoresNivel({ visible, onCancel }) {
    const [archivo, setArchivo] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [paso, setPaso] = useState(PASO_CARGA);
    const [resultado, setResultado] = useState(null);

    const handleCerrar = () => {
        setArchivo(null);
        setPaso(PASO_CARGA);
        setResultado(null);
        onCancel();
    };

    const propsUpload = {
        onRemove: () => setArchivo(null),
        beforeUpload: (file) => {
            const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                         || file.type === 'application/vnd.ms-excel';
            if (!isExcel) {
                message.error('Solo se permiten archivos Excel (.xlsx, .xls)');
                return false;
            }
            setArchivo(file);
            return false;
        },
        maxCount: 1,
        accept: '.xlsx,.xls',
        fileList: archivo ? [archivo] : [],
    };

    const handleActualizar = async () => {
        if (!archivo) {
            message.warning('Debe seleccionar un archivo');
            return;
        }
        try {
            setCargando(true);
            const formData = new FormData();
            formData.append('archivo', archivo);
            const res = await ColaboradorService.actualizarAprobadoresPorNivel(formData);
            setResultado(res);
            setPaso(PASO_RESULTADO);
        } catch (error) {
            message.error('Error al procesar: ' + (error.response?.data?.mensaje ?? error.message));
        } finally {
            setCargando(false);
        }
    };

    const columnasObservaciones = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (_, __, i) => i + 1,
        },
        {
            title: 'DNI Colaborador',
            dataIndex: 'numeroDocumentoIdentidad',
            key: 'dni',
            width: 150,
        },
        {
            title: 'Observación',
            dataIndex: 'observacion',
            key: 'observacion',
        },
    ];

    const footerCarga = [
        <Button key="cancelar" onClick={handleCerrar}>Cancelar</Button>,
        <Button
            key="actualizar"
            type="primary"
            onClick={handleActualizar}
            loading={cargando}
            disabled={!archivo}
        >
            Procesar
        </Button>,
    ];

    const footerResultado = [
        <Button key="cerrar" type="primary" onClick={handleCerrar}>Cerrar</Button>,
    ];

    return (
        <Modal
            title="Actualizar aprobadores por nivel"
            open={visible}
            onCancel={handleCerrar}
            width={700}
            footer={paso === PASO_CARGA ? footerCarga : footerResultado}
            maskClosable={false}
        >
            {paso === PASO_CARGA && (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                        <p><strong>Formato del archivo Excel:</strong></p>
                        <ul>
                            <li>Columna 1: <code>NUMERO_DOCUMENTO_IDENTIDAD</code> — DNI del colaborador</li>
                            <li>Columna 2: <code>JEFE</code> — DNI del jefe inmediato (opcional)</li>
                            <li>Columna 3: <code>APROBADORES_PRIMER_NIVEL</code> — DNIs separados por coma</li>
                            <li>Columna 4: <code>APROBADORES_SEGUNDO_NIVEL</code> — DNIs separados por coma</li>
                            <li>Columna 5: <code>APROBADORES_TERCER_NIVEL</code> — DNIs separados por coma</li>
                        </ul>
                    </div>
                    <Upload {...propsUpload}>
                        <Button icon={<UploadOutlined />}>Seleccionar archivo Excel</Button>
                    </Upload>
                </Space>
            )}

            {paso === PASO_RESULTADO && resultado && (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Row gutter={24}>
                        <Col span={8}>
                            <Statistic title="Procesados" value={resultado.totalProcesados} />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Actualizados"
                                value={resultado.totalActualizados}
                                valueStyle={{ color: '#3f8600' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Con observaciones"
                                value={resultado.observaciones?.length ?? 0}
                                valueStyle={{ color: resultado.observaciones?.length > 0 ? '#cf1322' : '#3f8600' }}
                                prefix={resultado.observaciones?.length > 0 ? <WarningOutlined /> : <CheckCircleOutlined />}
                            />
                        </Col>
                    </Row>

                    {resultado.observaciones?.length > 0 ? (
                        <>
                            <Alert
                                type="warning"
                                showIcon
                                message={`${resultado.observaciones.length} registro(s) presentaron observaciones. Revise el detalle a continuación.`}
                            />
                            <Table
                                columns={columnasObservaciones}
                                dataSource={resultado.observaciones}
                                rowKey="numeroDocumentoIdentidad"
                                pagination={{ pageSize: 10, showSizeChanger: false }}
                                size="small"
                                bordered
                                scroll={{ y: 300 }}
                            />
                        </>
                    ) : (
                        <Alert
                            type="success"
                            showIcon
                            message="Todos los registros fueron actualizados correctamente."
                        />
                    )}
                </Space>
            )}
        </Modal>
    );
}
